export function createGoogleReCaptcha(options) {
  return {
    install(app) {
      const instance = new GoogleReCaptcha(options);
      let loadReCaptchaPromise = null;

      /**
       * @param {string} badgePosition Valid values are "inline", "bottomleft" and "bottomright"
       * @returns {Promise<void>}
       */
      app.config.globalProperties.$recaptchaLoaded = function (badgePosition = 'bottomright') {
        if (!loadReCaptchaPromise) {
          loadReCaptchaPromise = instance.initReCaptcha(badgePosition);
        }

        return loadReCaptchaPromise;
      };

      app.config.globalProperties.$recaptcha = async function (action) {
        await this.$recaptchaLoaded();
        return instance.execute(action);
      };

      app.config.globalProperties.$recaptchaShowBadge = function () {
        instance.showBadge();
      };

      app.config.globalProperties.$recaptchaHideBadge = function () {
        instance.hideBadge();
      };

      app.config.globalProperties.$recaptchaOnError = function (callback) {
        instance.on('error', callback);
      };

      app.config.globalProperties.$recaptchaOnResponseExpire = function (callback) {
        instance.on('response-expire', callback);
      };
    },
  }
}

const UUID_PATTERN = /^[a-f\d]{8}-[a-f\d]{4}-[1-5][a-f\d]{3}-[89ab][a-f\d]{3}-[a-f\d]{12}$/i;

const isValidUuid = (value) => typeof value === 'string' && UUID_PATTERN.test(value);

const getCryptoApi = () => {
  try {
    return typeof crypto !== 'undefined' ? crypto : null;
  } catch {
    return null;
  }
};

export const createSecureUuid = (cryptoApi = getCryptoApi()) => {
  try {
    if (typeof cryptoApi?.randomUUID === 'function') {
      const uuid = cryptoApi.randomUUID();
      if (isValidUuid(uuid)) {
        return uuid;
      }
    }
  } catch {
    // Fall through to the secure random-values implementation.
  }

  try {
    if (typeof cryptoApi?.getRandomValues !== 'function') {
      return null;
    }

    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    const uuid = [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join('-');

    return isValidUuid(uuid) ? uuid : null;
  } catch {
    return null;
  }
};

class GoogleReCaptcha {
  /**
   * @param {PluginOptions} options
   */
  constructor(options) {
    this.isLoading = false;
    this.isLoaded = false;

    /** @type {HTMLStyleElement} */
    this.hiddenStyleContainer = document.createElement('style');
    this.hiddenStyleContainer.innerText = '.grecaptcha-badge { visibility:hidden !important; }';

    /** @type {HTMLStyleElement} */
    this.visibleStyleContainer = document.createElement('style');
    this.visibleStyleContainer.innerText = '.grecaptcha-badge { z-index: 25000; }';

    this.reCaptchaId = null;

    const errorCallbackId = this.randomUUID();
    const expiredResponseCallbackId = this.randomUUID();
    if (!errorCallbackId || !expiredResponseCallbackId) {
      throw new Error('Secure reCAPTCHA callback identifier unavailable.');
    }

    Object.defineProperties(this, {
      options: { value: Object.freeze(options) },
      errorCallbackName: { value: `__grecaptcha_error_cb_${errorCallbackId}` },
      expiredResponseCallbackName: { value: `__grecaptcha_expired_res_cb_${expiredResponseCallbackId}` },
      onErrorCallbacks: { value: [] },
      onResponseExpireCallbacks: { value: [] },
    });
  }

  hideBadge() {
    if (document.head.contains(this.visibleStyleContainer)) {
      document.head.removeChild(this.visibleStyleContainer);
    }

    document.head.appendChild(this.hiddenStyleContainer);
  }

  showBadge() {
    if (document.head.contains(this.hiddenStyleContainer)) {
      document.head.removeChild(this.hiddenStyleContainer);
    }

    document.head.appendChild(this.visibleStyleContainer);
  }

  initReCaptcha(badgePosition) {
    if (this.isLoading) throw new Error('reCaptcha is already loading');
    if (this.isLoaded) throw new Error('reCaptcha is already loaded');

    this.isLoading = true;

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `https://www.google.com/recaptcha/api.js?render=explicit&badge=${badgePosition}`;
      script.addEventListener('error', () => {
        reject(new Error('ReCaptcha script has failed to load!'));
      });
      script.addEventListener('load', () => {
        this.waitForScriptToLoad().then(() => {
          this.renderReCaptcha();
          this.isLoaded = true;
          resolve();
        });
      });

      document.head.appendChild(script);
    }).finally(() => {
      this.isLoading = false;
    });
  }

  renderReCaptcha() {
    const instance = this;

    Object.defineProperties(window, {
      [this.expiredResponseCallbackName]: {
        value: function() {
          for (const callback of instance.onResponseExpireCallbacks) {
            callback();
          }
        }
      },
      [this.errorCallbackName]: {
        value: function () {
          for (const callback of instance.onErrorCallbacks) {
            callback();
          }
        },
      },
    });

    this.reCaptchaId = window.grecaptcha.render({
      'sitekey': this.options.siteKey,
      'expired-callback': this.expiredResponseCallbackName,
      'error-callback': this.errorCallbackName,
    });
  }

  execute(action) {
    console.assert(this.reCaptchaId !== null, 'ReCAPTCHA is still not rendered!');
    return window.grecaptcha.execute(this.reCaptchaId, { action });
  }

  waitForScriptToLoad() {
    return new Promise((resolve) => {
      const pollWindowIntervalHandle = setInterval(() => {
        if (typeof window.grecaptcha !== 'undefined') {
          window.grecaptcha.ready(() => {
            resolve();
          });

          clearInterval(pollWindowIntervalHandle);
        }
      }, 25);
    });
  }

  randomUUID() {
    const uuid = createSecureUuid();
    return uuid ? uuid.replace(/-/g, '') : null;
  }

  on(type, callback) {
    console.assert(typeof callback === 'function');

    switch (type) {
      case 'error':
        this.onErrorCallbacks.push(callback);
        break;
      case 'response-expire':
        this.onResponseExpireCallbacks.push(callback);
        break;
      default:
        throw new Error(`Unsupported event type '${type}'`);
    }
  }
}
